import {
  AccountType,
  App,
  KitchenProductStatus,
  OrderProductStatus,
  PaymentTerm,
  TableStatus,
  VariantType,
} from "../models";

export const CURRENT_CLIENT = "row-current-client";
export const APP_LANGUAGE = "row-app-language";
export const APP_THEME = "row-app-theme";
export const DEFAULT_LANGUAGE = "en";
export const PAYLOAD_DATE_FORMAT = "yyyy-MM-dd";
export const DEFAULT_CHART_FILTER_TYPE =
  process.env.REACT_APP_ENV === "DEMO" ? "A" : "T";
export const TABLE_TYPE = "row-table-type";
export const SALARY_CONFIG = "row-salary-config";
export const SHOW_PRODUCTS = "row-show-products";
export const LAST_FEEDBACK_TIME = "last-feedback-time";

/**
 * RTK Query cache freshness window, in seconds.
 *
 * Used as the api slices' `refetchOnMountOrArgChange`. A number (rather than
 * `true`) means a mounting hook only refires when its cached entry is older
 * than this, so remounts, back-navigation and tab switches inside the window
 * are served from the cache that `keepUnusedDataFor` is holding on to.
 * Endpoints that declare `providesTags`/`invalidatesTags` stay exact
 * regardless; this is the freshness floor for the ones that do not.
 */
export const CACHE_STALE_AFTER = 60;

/**
 * How long an unused cache entry survives after its last subscriber unmounts.
 */
export const CACHE_KEEP_UNUSED = 3600;

/** Number of slices the dashboard stock chart asks the server to rank. */
export const STOCK_CHART_TOP_N = 12;

export const FILE_BASE_URL = {
  LOGO: process.env.REACT_APP_S3_LOGO_URL,
  PROFILE_PICTURE: process.env.REACT_APP_S3_PICTURE_URL,
  ORDER: process.env.REACT_APP_S3_ORDER_URL,
  PURCHASE: process.env.REACT_APP_S3_PURCHASE_URL,
  CATEGORY_IMAGE: process.env.REACT_APP_S3_CATEGORY_IMAGE_URL,
  PRODUCT_IMAGE: process.env.REACT_APP_S3_PRODUCT_IMAGE_URL,
};

export const PRINTBILL_FONT_SIZE = {
  NORMAL: "11px",
  LARGE: "13px",
  SMALL: "10px",
};

export const PRINTKOT_FONT_SIZE = {
  NORMAL: "16px",
  LARGE: "19px",
  SMALL: "12px",
};

export const VALUES = {
  // FIELD_BORDER_OPACITY: 0.32,
  FIELD_BORDER_OPACITY: 0.65,
};

export const getCurrencyOptions = (t: any) => [
  { value: "USD", label: "USD	(US Dollar) $" },
  { value: "INR", label: "INR	(Indian Rupee) ₹" },
  { value: "EUR", label: "EUR	(Euro) €" },
  { value: "GBP", label: "GBP	(Pound Sterling) £" },
  { value: "AUD", label: "AUD	(Australian Dollar) $" },
  { value: "CAD", label: "CAD	(Canadian Dollar) $" },
  { value: "SGD", label: "SGD	(Singapore Dollar) $" },
  { value: "CHF", label: "CHF	(Swiss Franc) CHF" },
  { value: "MYR", label: "MYR	(Malaysian Ringgit) RM" },
  { value: "JPY", label: "JPY	(Yen) ¥" },
  { value: "CNY", label: "CNY	(Yuan Renminbi) ¥" },
  { value: "AED", label: "AED	(UAE Dirham)" },
  { value: "AFN", label: "AFN	(Afghani)	؋" },
  { value: "ALL", label: "ALL	(Lek) Lek" },
  { value: "AMD", label: "AMD	(Armenian Dram)" },
  { value: "ANG", label: "ANG	(Netherlands Antillian Guilder) ƒ" },
  { value: "AOA", label: "AOA	(Kwanza)" },
  { value: "ARS", label: "ARS	(Argentine Peso) $" },
  { value: "AWG", label: "AWG	(Aruban Guilder) ƒ" },
  { value: "AZN", label: "AZN	(Azerbaijanian Manat) ман" },
  { value: "BAM", label: "BAM	(Convertible Marks) KM" },
  { value: "BBD", label: "BBD	(Barbados Dollar) $" },
  { value: "BDT", label: "BDT	(Taka)" },
  { value: "BGN", label: "BGN	(Bulgarian Lev) лв" },
  { value: "BHD", label: "BHD	(Bahraini Dinar)" },
  { value: "BIF", label: "BIF	(Burundi Franc)" },
  {
    value: "BMD",
    label: "BMD	(Bermudian Dollar (customarily known as Bermuda Dollar)) $",
  },
  { value: "BND", label: "BND	(Brunei Dollar) $" },
  { value: "BOB", label: "BOB (BOV Boliviano Mvdol) $b" },
  { value: "BRL", label: "BRL	(Brazilian Real) R$" },
  { value: "BSD", label: "BSD	(Bahamian Dollar) $" },
  { value: "BWP", label: "BWP	(Pula) P" },
  { value: "BYR", label: "BYR	(Belarussian Ruble) p." },
  { value: "BZD", label: "BZD	(Belize Dollar) BZ$" },
  { value: "CDF", label: "CDF	(Congolese Franc)" },
  { value: "CLP", label: "CLP (CLF Chilean Peso Unidades de fomento) $" },
  {
    value: "COP",
    label: "COP (COU Colombian Peso Unidad de Valor Real) $",
  },
  { value: "CRC", label: "CRC	(Costa Rican Colon) ₡" },
  { value: "CUP", label: "CUP (CUC Cuban Peso Peso Convertible) ₱" },
  { value: "CVE", label: "CVE	(Cape Verde Escudo)" },
  { value: "CZK", label: "CZK	(Czech Koruna) Kč" },
  { value: "DJF", label: "DJF	(Djibouti Franc)" },
  { value: "DKK", label: "DKK	(Danish Krone) kr" },
  { value: "DOP", label: "DOP	(Dominican Peso) RD$" },
  { value: "DZD", label: "DZD	(Algerian Dinar)" },
  { value: "EEK", label: "EEK	(Kroon)" },
  { value: "EGP", label: "EGP	(Egyptian Pound) £" },
  { value: "ERN", label: "ERN	(Nakfa)" },
  { value: "ETB", label: "ETB	(Ethiopian Birr)" },
  { value: "FJD", label: "FJD	(Fiji Dollar) $" },
  { value: "FKP", label: "FKP	(Falkland Islands Pound) £" },
  { value: "GEL", label: "GEL	(Lari)" },
  { value: "GHS", label: "GHS	(Cedi)" },
  { value: "GIP", label: "GIP	(Gibraltar Pound) £" },
  { value: "GMD", label: "GMD	(Dalasi)" },
  { value: "GNF", label: "GNF	(Guinea Franc)" },
  { value: "GTQ", label: "GTQ	(Quetzal) Q" },
  { value: "GYD", label: "GYD	(Guyana Dollar) $" },
  { value: "HKD", label: "HKD	(Hong Kong Dollar) $" },
  { value: "HNL", label: "HNL	(Lempira) L" },
  { value: "HRK", label: "HRK	(Croatian Kuna) kn" },
  { value: "HTG", label: "HTG (USD Gourde US Dollar)" },
  { value: "HUF", label: "HUF	(Forint) Ft" },
  { value: "IDR", label: "IDR	(Rupiah) Rp" },
  { value: "ILS", label: "ILS	(New Israeli Sheqel) ₪" },
  { value: "IQD", label: "IQD	(Iraqi Dinar)" },
  { value: "IRR", label: "IRR	(Iranian Rial	) " },
  { value: "ISK", label: "ISK	(Iceland Krona) kr" },
  { value: "JMD", label: "JMD	(Jamaican Dollar) J$" },
  { value: "JOD", label: "JOD	(Jordanian Dinar)" },
  { value: "KES", label: "KES	(Kenyan Shilling)" },
  { value: "KGS", label: "KGS	(Som) лв" },
  { value: "KHR", label: "KHR	(Riel) ៛" },
  { value: "KMF", label: "KMF	(Comoro Franc)" },
  { value: "KPW", label: "KPW	(North Korean Won) ₩" },
  { value: "KRW", label: "KRW	(Won) ₩" },
  { value: "KWD", label: "KWD	(Kuwaiti Dinar) " },
  { value: "KYD", label: "KYD	(Cayman Islands Dollar) $" },
  { value: "KZT", label: "KZT	(Tenge) лв" },
  { value: "LAK", label: "LAK	(Kip) ₭" },
  { value: "LBP", label: "LBP	(Lebanese Pound) £" },
  { value: "LKR", label: "LKR	(Sri Lanka Rupee) ₨" },
  { value: "LRD", label: "LRD	(Liberian Dollar) $" },
  { value: "LTL", label: "LTL	(Lithuanian Litas) Lt" },
  { value: "LVL", label: "LVL	(Latvian Lats) Ls" },
  { value: "LYD", label: "LYD	(Libyan Dinar)" },
  { value: "MAD", label: "MAD	(Moroccan Dirham)" },
  { value: "MDL", label: "MDL	(Moldovan Leu)" },
  { value: "MGA", label: "MGA	(Malagasy Ariary)" },
  { value: "MKD", label: "MKD	(Denar) ден" },
  { value: "MMK", label: "MMK	(Kyat)" },
  { value: "MNT", label: "MNT	(Tugrik) ₮" },
  { value: "MOP", label: "MOP	(Pataca)" },
  { value: "MRO", label: "MRO	(Ouguiya)" },
  { value: "MUR", label: "MUR	(Mauritius Rupee) ₨" },
  { value: "MVR", label: "MVR	(Rufiyaa)" },
  { value: "MWK", label: "MWK	(Kwacha)" },
  {
    value: "MXN",
    label: "MXN (MXV	Mexican Peso Mexican Unidad de Inversion (UDI)) $",
  },
  { value: "MZN", label: "MZN	(Metical) MT" },
  { value: "NGN", label: "NGN	(Naira) ₦" },
  { value: "NIO", label: "NIO	(Cordoba Oro) C$" },
  { value: "NOK", label: "NOK	(Norwegian Krone) kr" },
  { value: "NPR", label: "NPR	(Nepalese Rupee) ₨" },
  { value: "NZD", label: "NZD	(New Zealand Dollar) $" },
  { value: "OMR", label: "OMR	(Rial Omani) ﷼" },
  { value: "PAB", label: "PAB (USD	Balboa US Dollar) B/." },
  { value: "PEN", label: "PEN	(Nuevo Sol) S/." },
  { value: "PGK", label: "PGK	(Kina)" },
  { value: "PHP", label: "PHP	(Philippine Peso) Php" },
  { value: "PKR", label: "PKR	(Pakistan Rupee) ₨" },
  { value: "PLN", label: "PLN	(Zloty) zł" },
  { value: "PYG", label: "PYG	(Guarani) Gs" },
  { value: "QAR", label: "QAR	(Qatari Rial)	﷼" },
  { value: "RON", label: "RON	(New Leu) lei" },
  { value: "RSD", label: "RSD	(Serbian Dinar)	Дин." },
  { value: "RUB", label: "RUB	(Russian Ruble) руб" },
  { value: "RWF", label: "RWF	(Rwanda Franc)" },
  { value: "SAR", label: "SAR	(Saudi Riyal)	﷼" },
  { value: "SBD", label: "SBD	(Solomon Islands Dollar) $" },
  { value: "SCR", label: "SCR	(Seychelles Rupee) ₨" },
  { value: "SDG", label: "SDG	(Sudanese Pound)" },
  { value: "SEK", label: "SEK	(Swedish Krona) kr" },
  { value: "SHP", label: "SHP	(Saint Helena Pound) £" },
  { value: "SLL", label: "SLL	(Leone)" },
  { value: "SOS", label: "SOS	(Somali Shilling)	S" },
  { value: "SRD", label: "SRD	(Surinam Dollar) $" },
  { value: "STD", label: "STD	(Dobra) " },
  { value: "SVC", label: "SVC (USD	El Salvador Colon US Dollar) $" },
  { value: "SYP", label: "SYP	(Syrian Pound) £" },
  { value: "SZL", label: "SZL	(Lilangeni)" },
  { value: "THB", label: "THB	(Baht) ฿" },
  { value: "TJS", label: "TJS	(Somoni)" },
  { value: "TMT", label: "TMT	(Manat)" },
  { value: "TND", label: "TND	(Tunisian Dinar)" },
  { value: "TOP", label: "TOP	(Pa'anga)" },
  { value: "TRY", label: "TRY	(Turkish Lira) TL" },
  { value: "TTD", label: "TTD	(Trinidad and Tobago Dollar) TT$" },
  { value: "TWD", label: "TWD	(New Taiwan Dollar) NT$" },
  { value: "TZS", label: "TZS	(Tanzanian Shilling)" },
  { value: "UAH", label: "UAH	(Hryvnia)	₴" },
  { value: "UGX", label: "UGX	(Uganda Shilling)" },
  {
    value: "UYU",
    label: "UYU (UYI	Peso Uruguayo Uruguay Peso en Unidades Indexadas)	$U",
  },
  { value: "UZS", label: "UZS	(Uzbekistan Sum) лв" },
  { value: "VEF", label: "VEF	(Bolivar Fuerte) Bs" },
  { value: "VND", label: "VND	(Dong) ₫" },
  { value: "VUV", label: "VUV	(Vatu)" },
  { value: "WST", label: "WST	(Tala)" },
  { value: "XAF", label: "XAF	(CFA Franc BEAC)" },
  { value: "XAG", label: "XAG	(Silver)" },
  { value: "XAU", label: "XAU	(Gold	)" },
  {
    value: "XBA",
    label: "XBA	(Bond Markets Units European Composite Unit (EURCO))",
  },
  { value: "XBB", label: "XBB	(European Monetary Unit (E.M.U.-6))" },
  { value: "XBC", label: "XBC	(European Unit of Account 9(E.U.A.-9))" },
  { value: "XBD", label: "XBD	(European Unit of Account 17(E.U.A.-17))" },
  { value: "XCD", label: "XCD	(East Caribbean Dollar) $" },
  { value: "XDR", label: "XDR	(SDR)" },
  { value: "XFU", label: "XFU	(UIC-Franc)" },
  { value: "XOF", label: "XOF	(CFA Franc BCEAO)" },
  { value: "XPD", label: "XPD	(Palladium)" },
  { value: "XPF", label: "XPF	(CFP Franc)" },
  { value: "XPT", label: "XPT	(Platinum)" },
  { value: "YER", label: "YER	(Yemeni Rial)	﷼" },
  { value: "ZAR", label: "ZAR	(Rand) R" },
  { value: "ZAR", label: "ZAR (LSL	Rand Loti)" },
  { value: "ZAR", label: "ZAR (NAD	Rand Namibia Dollar)" },
  { value: "ZMK", label: "ZMK	(Zambian Kwacha)" },
  { value: "ZWL", label: "ZWL	(Zimbabwe Dollar)" },
];

export const currencySymbolMap: Record<string, string> = {
  USD: "$",
  INR: "₹",
  EUR: "€",
  GBP: "£",
  AUD: "$",
  CAD: "$",
  SGD: "$",
  CHF: "CHF",
  MYR: "RM",
  JPY: "¥",
  CNY: "¥",
  AED: "د.إ",
  AFN: "؋",
  ALL: "Lek",
  AMD: "֏",
  ANG: "ƒ",
  AOA: "Kz",
  ARS: "$",
  AWG: "ƒ",
  AZN: "₼",
  BAM: "KM",
  BBD: "$",
  BDT: "৳",
  BGN: "лв",
  BHD: ".د.ب",
  BIF: "FBu",
  BMD: "$",
  BND: "$",
  BOB: "Bs",
  BRL: "R$",
  BSD: "$",
  BWP: "P",
  BYR: "Br",
  BZD: "BZ$",
  CDF: "FC",
  CLP: "$",
  COP: "$",
  CRC: "₡",
  CUP: "₱",
  CVE: "$",
  CZK: "Kč",
  DJF: "Fdj",
  DKK: "kr",
  DOP: "RD$",
  DZD: "دج",
  EEK: "kr",
  EGP: "£",
  ERN: "Nfk",
  ETB: "Br",
  FJD: "$",
  FKP: "£",
  GEL: "₾",
  GHS: "₵",
  GIP: "£",
  GMD: "D",
  GNF: "FG",
  GTQ: "Q",
  GYD: "$",
  HKD: "$",
  HNL: "L",
  HRK: "kn",
  HTG: "G",
  HUF: "Ft",
  IDR: "Rp",
  ILS: "₪",
  IQD: "ع.د",
  IRR: "﷼",
  ISK: "kr",
  JMD: "J$",
  JOD: "",
  KES: "Sh",
  KGS: "лв",
  KHR: "៛",
  KMF: "CF",
  KPW: "₩",
  KRW: "₩",
  KWD: "",
  KYD: "$",
  KZT: "₸",
  LAK: "₭",
  LBP: "ل.ل",
  LKR: "₨",
  LRD: "$",
  LTL: "Lt",
  LVL: "Ls",
  LYD: "",
  MAD: "",
  MDL: "L",
  MGA: "Ar",
  MKD: "ден",
  MMK: "Ks",
  MNT: "₮",
  MOP: "P",
  MRO: "UM",
  MUR: "₨",
  MVR: "Rf",
  MWK: "MK",
  MXN: "$",
  MZN: "MT",
  NGN: "₦",
  NIO: "C$",
  NOK: "kr",
  NPR: "₨",
  NZD: "$",
  OMR: "﷼",
  PAB: "B/.",
  PEN: "S/.",
  PGK: "K",
  PHP: "Php",
  PKR: "₨",
  PLN: "zł",
  PYG: "₲",
  QAR: "﷼",
  RON: "lei",
  RSD: "Дин.",
  RUB: "₽",
  RWF: "FRw",
  SAR: "﷼",
  SBD: "$",
  SCR: "₨",
  SDG: "ج.س.",
  SEK: "kr",
  SHP: "£",
  SLL: "Le",
  SOS: "Sh",
  SRD: "$",
  STD: "Db",
  SVC: "$",
  SYP: "£",
  SZL: "L",
  THB: "฿",
  TJS: "SM",
  TMT: "m",
  TND: "د.ت",
  TOP: "T$",
  TRY: "₺",
  TTD: "TT$",
  TWD: "NT$",
  TZS: "Sh",
  UAH: "₴",
  UGX: "Sh",
  UYU: "$U",
  UZS: "лв",
  VEF: "Bs",
  VND: "₫",
  VUV: "Vt",
  WST: "T",
  XAF: "FCFA",
  XAG: "Ag",
  XAU: "Au",
  XBA: "",
  XBB: "",
  XBC: "",
  XBD: "",
  XCD: "$",
  XDR: "SDR",
  XFU: "",
  XOF: "CFA",
  XPD: "Pd",
  XPF: "₣",
  XPT: "Pt",
  YER: "﷼",
  ZAR: "R",
  ZMK: "ZK",
  ZWL: "Z$",
};

export const currencyDenominations: any = {
  INR: [2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  USD: [100, 50, 20, 10, 5, 2, 1, 0.5, 0.25, 0.1, 0.05, 0.01],
  EUR: [500, 200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01],
  GBP: [50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01],
  AUD: [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05],
  CAD: [100, 50, 20, 10, 5, 2, 1, 0.25, 0.1, 0.05],
  SGD: [1000, 100, 50, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05],
  CHF: [1000, 200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05],
  MYR: [100, 50, 20, 10, 5, 1, 0.5, 0.2, 0.1, 0.05],
  JPY: [10000, 5000, 2000, 1000, 500, 100, 50, 10, 5, 1],
  CNY: [100, 50, 20, 10, 5, 1, 0.5, 0.1],
  AED: [1000, 500, 200, 100, 50, 20, 10, 5, 1, 0.5, 0.25],
  AFN: [1000, 500, 100, 50, 20, 10, 5, 2, 1],
  ALL: [10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 1],
  AMD: [
    100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10,
  ],
  ANG: [250, 100, 50, 25, 10, 5, 1, 0.25, 0.1, 0.05],
  AOA: [10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5],
  ARS: [20000, 10000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  AWG: [500, 100, 50, 25, 10, 5, 1, 0.5, 0.25, 0.1, 0.05],
  AZN: [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.03, 0.01],
  BAM: [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05],
  BBD: [100, 50, 20, 10, 5, 2, 1, 0.25, 0.1, 0.05, 0.01],
  BDT: [1000, 500, 100, 50, 20, 10, 5, 2, 1],
  BGN: [100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01],
  BHD: [20, 10, 5, 1, 0.5, 0.1, 0.05],
  BIF: [10000, 5000, 2000, 1000, 500, 100, 50, 10],
  BMD: [100, 50, 20, 10, 5, 2, 1, 0.25, 0.1, 0.05, 0.01],
  BND: [10000, 1000, 500, 100, 50, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.01],
  BOB: [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1],
  BRL: [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.25, 0.1, 0.05],
  BSD: [100, 50, 20, 10, 5, 1, 0.25, 0.1, 0.05, 0.01],
  BWP: [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.25, 0.1, 0.05],
  BYR: [200000, 100000, 50000, 20000, 10000, 5000, 1000],
  BZD: [100, 50, 20, 10, 5, 2, 1, 0.5, 0.25, 0.1, 0.05, 0.01],
  CDF: [20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50],
  CLP: [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 10],
  COP: [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50],
  CRC: [50000, 20000, 10000, 5000, 2000, 1000, 500, 100, 50, 25, 10, 5],
  CUP: [1000, 500, 200, 100, 50, 20, 10, 5, 3, 1],
  CVE: [5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 1],
  CZK: [5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  DJF: [10000, 5000, 2000, 1000, 500, 250, 100, 50, 20, 10, 5, 1],
  DKK: [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  DOP: [2000, 1000, 500, 200, 100, 50, 25, 10, 5, 1],
  DZD: [2000, 1000, 500, 200, 100, 50, 20, 10, 5],
  EEK: [500, 100, 50, 25, 10, 5, 2, 1],
  EGP: [200, 100, 50, 20, 10, 5, 1],
  ERN: [100, 50, 10, 5, 1],
  ETB: [200, 100, 50, 10, 5, 1],
  FJD: [100, 50, 20, 10, 5, 2, 1],
  FKP: [100, 50, 20, 10, 5, 2, 1],
  GEL: [200, 100, 50, 20, 10, 5, 2, 1],
  GHS: [200, 100, 50, 20, 10, 5, 2, 1],
  GIP: [100, 50, 20, 10, 5, 2, 1],
  GMD: [200, 100, 50, 25, 20, 10, 5, 1],
  GNF: [20000, 10000, 5000, 2000, 1000, 500, 100],
  GTQ: [200, 100, 50, 20, 10, 5, 1],
  GYD: [5000, 1000, 500, 100, 20, 10, 5, 1],
  HKD: [1000, 500, 100, 50, 20, 10, 5, 2, 1],
  HNL: [500, 100, 50, 20, 10, 5, 2, 1],
  HRK: [1000, 500, 200, 100, 50, 25, 10, 5, 2, 1],
  HTG: [1000, 500, 250, 100, 50, 25, 10, 5, 1],
  HUF: [20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5],
  IDR: [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100],
  ILS: [200, 100, 50, 20, 10, 5, 2, 1],
  IQD: [50000, 25000, 10000, 5000, 1000, 500, 250, 100, 50, 25],
  IRR: [1000000, 500000, 100000, 50000, 20000, 10000, 5000, 2000, 1000],
  ISK: [10000, 5000, 2000, 1000, 500, 100, 50, 10, 5, 1],
  JMD: [5000, 1000, 500, 100, 50, 20, 10, 5, 1],
  JOD: [50, 20, 10, 5, 1],
  KES: [1000, 500, 200, 100, 50, 40, 20, 10, 5, 1],
  KGS: [5000, 1000, 500, 200, 100, 50, 20, 10, 5, 3, 1],
  KHR: [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 100, 50],
  KMF: [10000, 5000, 2000, 1000, 500, 250, 100, 50, 25],
  KPW: [5000, 2000, 1000, 500, 200, 100, 50, 10, 5, 1],
  KRW: [50000, 10000, 5000, 1000, 500, 100, 50, 10],
  KWD: [20, 10, 5, 1],
  KYD: [100, 50, 25, 10, 5, 1],
  KZT: [20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10],
  LAK: [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 100],
  LBP: [100000, 50000, 20000, 10000, 5000, 1000, 500, 250],
  LKR: [5000, 1000, 500, 100, 50, 20, 10, 5, 2, 1],
  LRD: [500, 100, 50, 20, 10, 5, 1],
  LTL: [500, 200, 100, 50, 20, 10, 5, 2, 1],
  LVL: [500, 100, 50, 20, 10, 5, 2, 1],
  LYD: [20, 10, 5, 1],
  MAD: [200, 100, 50, 20, 10, 5, 2, 1],
  MDL: [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  MGA: [20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50],
  MKD: [5000, 1000, 500, 100, 50, 10, 5, 2, 1],
  MMK: [10000, 5000, 1000, 500, 200, 100, 50, 20, 10, 5, 1],
  MNT: [20000, 10000, 5000, 1000, 500, 100, 50, 20, 10, 5, 1],
  MOP: [1000, 500, 100, 50, 20, 10, 5, 2, 1],
  MRO: [5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 1],
  MUR: [2000, 1000, 500, 200, 100, 50, 25, 20, 10, 5, 1],
  MVR: [500, 100, 50, 20, 10, 5, 2, 1],
  MWK: [5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 1],
  MXN: [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  MZN: [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  NGN: [1000, 500, 200, 100, 50, 20, 10, 5],
  NIO: [1000, 500, 200, 100, 50, 20, 10, 5, 1],
  NOK: [1000, 500, 200, 100, 50, 20, 10, 5, 1],
  NPR: [1000, 500, 100, 50, 25, 20, 10, 5, 2, 1],
  NZD: [100, 50, 20, 10, 5, 2, 1],
  OMR: [50, 20, 10, 5, 1],
  PAB: [100, 50, 20, 10, 5, 1],
  PEN: [200, 100, 50, 20, 10, 5, 2, 1],
  PGK: [100, 50, 20, 10, 5, 2, 1],
  PHP: [1000, 500, 200, 100, 50, 20, 10, 5, 1],
  PKR: [5000, 1000, 500, 100, 50, 20, 10, 5, 2, 1],
  PLN: [500, 200, 100, 50, 20, 10, 5, 2, 1],
  PYG: [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 100],
  QAR: [500, 200, 100, 50, 10, 5, 1],
  RON: [500, 200, 100, 50, 10, 5, 1],
  RSD: [5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  RUB: [5000, 2000, 1000, 500, 200, 100, 50, 10, 5, 2, 1],
  RWF: [5000, 2000, 1000, 500, 100, 50, 20, 10, 5, 1],
  SAR: [500, 100, 50, 10, 5, 1],
  SBD: [100, 50, 20, 10, 5, 2, 1],
  SCR: [500, 100, 50, 25, 10, 5, 1],
  SDG: [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  SEK: [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  SHP: [20, 10, 5, 2, 1],
  SLL: [20000, 10000, 5000, 2000, 1000, 500, 100],
  SOS: [1000, 500, 100, 50, 10, 5, 1],
  SRD: [200, 100, 50, 20, 10, 5, 1],
  STD: [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 100],
  SVC: [200, 100, 50, 25, 10, 5, 1],
  SYP: [5000, 2000, 1000, 500, 200, 100, 50, 25, 10, 5, 1],
  SZL: [200, 100, 50, 20, 10, 5, 2, 1],
  THB: [1000, 500, 100, 50, 20, 10, 5, 2, 1],
  TJS: [500, 200, 100, 50, 20, 10, 5, 3, 1],
  TMT: [500, 100, 50, 20, 10, 5, 2, 1],
  TND: [50, 20, 10, 5, 2, 1],
  TOP: [100, 50, 20, 10, 5, 2, 1],
  TRY: [200, 100, 50, 20, 10, 5, 1],
  TTD: [100, 50, 20, 10, 5, 1],
  TWD: [2000, 1000, 500, 100, 50, 10, 5, 1],
  TZS: [10000, 5000, 2000, 1000, 500, 200, 100, 50],
  UAH: [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  UGX: [50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100],
  UYU: [2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  UZS: [200000, 100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100],
  VEF: [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 100],
  VND: [
    500000, 200000, 100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200,
  ],
  VUV: [10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1],
  WST: [100, 50, 20, 10, 5, 2, 1],
  XAF: [10000, 5000, 2000, 1000, 500, 100, 50, 25, 10, 5, 1],
  XAG: [],
  XAU: [],
  XBA: [],
  XBB: [],
  XBC: [],
  XBD: [],
  XCD: [100, 50, 20, 10, 5, 2, 1],
  XDR: [],
  XFU: [],
  XOF: [10000, 5000, 2000, 1000, 500, 200, 100, 50, 25, 10, 5, 1],
  XPD: [],
  XPT: [],
  XPF: [10000, 5000, 1000, 500, 100, 50, 20, 10, 5, 2, 1],
  YER: [1000, 500, 250, 200, 100, 50, 20, 10, 5, 1],
  ZAR: [200, 100, 50, 20, 10, 5, 2, 1],
  ZMK: [50000, 20000, 10000, 5000, 1000, 500, 100],
  ZWL: [100, 50, 20, 10, 5, 2, 1],
};

export const TIMEZONES = [
  { value: "UTC", label: "(GMT+00:00) UTC" },

  // Asia / Oceania
  { value: "Asia/Kolkata", label: "(GMT+05:30) Asia/Kolkata" },
  { value: "Asia/Dubai", label: "(GMT+04:00) Asia/Dubai" },
  { value: "Asia/Shanghai", label: "(GMT+08:00) Asia/Shanghai" },
  { value: "Asia/Tokyo", label: "(GMT+09:00) Asia/Tokyo" },
  { value: "Asia/Singapore", label: "(GMT+08:00) Asia/Singapore" },
  { value: "Asia/Kuala_Lumpur", label: "(GMT+08:00) Asia/Kuala_Lumpur" },
  { value: "Asia/Seoul", label: "(GMT+09:00) Asia/Seoul" },
  { value: "Asia/Hong_Kong", label: "(GMT+08:00) Asia/Hong_Kong" },
  { value: "Asia/Jakarta", label: "(GMT+07:00) Asia/Jakarta" },
  { value: "Asia/Manila", label: "(GMT+08:00) Asia/Manila" },
  { value: "Asia/Colombo", label: "(GMT+05:30) Asia/Colombo" },
  { value: "Australia/Sydney", label: "(GMT+11:00) Australia/Sydney" },
  { value: "Australia/Melbourne", label: "(GMT+11:00) Australia/Melbourne" },
  { value: "Australia/Perth", label: "(GMT+08:00) Australia/Perth" },
  { value: "Australia/Brisbane", label: "(GMT+10:00) Australia/Brisbane" },
  { value: "Pacific/Auckland", label: "(GMT+13:00) Pacific/Auckland" },

  // Americas
  { value: "America/New_York", label: "(GMT-05:00) America/New_York" },
  { value: "America/Chicago", label: "(GMT-06:00) America/Chicago" },
  { value: "America/Denver", label: "(GMT-07:00) America/Denver" },
  { value: "America/Los_Angeles", label: "(GMT-08:00) America/Los_Angeles" },
  { value: "America/Toronto", label: "(GMT-05:00) America/Toronto" },
  { value: "America/Vancouver", label: "(GMT-08:00) America/Vancouver" },
  { value: "America/Sao_Paulo", label: "(GMT-03:00) America/Sao_Paulo" },
  { value: "America/Mexico_City", label: "(GMT-06:00) America/Mexico_City" },
  {
    value: "America/Argentina/Buenos_Aires",
    label: "(GMT-03:00) America/Argentina/Buenos_Aires",
  },
  { value: "America/Caracas", label: "(GMT-04:30) America/Caracas" },
  { value: "America/Bogota", label: "(GMT-05:00) America/Bogota" },

  // Europe
  { value: "Europe/London", label: "(GMT+00:00) Europe/London" },
  { value: "Europe/Berlin", label: "(GMT+01:00) Europe/Berlin" },
  { value: "Europe/Paris", label: "(GMT+01:00) Europe/Paris" },
  { value: "Europe/Madrid", label: "(GMT+01:00) Europe/Madrid" },
  { value: "Europe/Rome", label: "(GMT+01:00) Europe/Rome" },
  { value: "Europe/Moscow", label: "(GMT+03:00) Europe/Moscow" },
  { value: "Europe/Istanbul", label: "(GMT+03:00) Europe/Istanbul" },
  { value: "Europe/Warsaw", label: "(GMT+01:00) Europe/Warsaw" },
  { value: "Europe/Zurich", label: "(GMT+01:00) Europe/Zurich" },
  { value: "Europe/Amsterdam", label: "(GMT+01:00) Europe/Amsterdam" },
  { value: "Europe/Brussels", label: "(GMT+01:00) Europe/Brussels" },

  // Africa / Middle East
  { value: "Africa/Johannesburg", label: "(GMT+02:00) Africa/Johannesburg" },
  { value: "Africa/Cairo", label: "(GMT+02:00) Africa/Cairo" },
  { value: "Africa/Lagos", label: "(GMT+01:00) Africa/Lagos" },
  { value: "Africa/Nairobi", label: "(GMT+03:00) Africa/Nairobi" },
  { value: "Africa/Khartoum", label: "(GMT+02:00) Africa/Khartoum" },
  { value: "Africa/Accra", label: "(GMT+00:00) Africa/Accra" },
  { value: "Asia/Riyadh", label: "(GMT+03:00) Asia/Riyadh" },
  { value: "Asia/Tehran", label: "(GMT+03:30) Asia/Tehran" },
];

export const workingDaysPerMonthOptions = [
  { value: 0, label: "0" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
  { value: 8, label: "8" },
  { value: 9, label: "9" },
  { value: 10, label: "10" },
  { value: 11, label: "11" },
  { value: 12, label: "12" },
  { value: 13, label: "13" },
  { value: 14, label: "14" },
  { value: 15, label: "15" },
  { value: 16, label: "16" },
  { value: 17, label: "17" },
  { value: 18, label: "18" },
  { value: 19, label: "19" },
  { value: 20, label: "20" },
  { value: 21, label: "21" },
  { value: 22, label: "22" },
  { value: 23, label: "23" },
  { value: 24, label: "24" },
  { value: 25, label: "25" },
  { value: 26, label: "26" },
  { value: 27, label: "27" },
  { value: 28, label: "28" },
  { value: 29, label: "29" },
  { value: 30, label: "30" },
  { value: 31, label: "31" },
];

export const workingHoursPerDayOptions = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
  { value: 8, label: "8" },
  { value: 9, label: "9" },
  { value: 10, label: "10" },
  { value: 11, label: "11" },
  { value: 12, label: "12" },
  { value: 13, label: "13" },
  { value: 14, label: "14" },
  { value: 15, label: "15" },
  { value: 16, label: "16" },
  { value: 17, label: "17" },
  { value: 18, label: "18" },
  { value: 19, label: "19" },
  { value: 20, label: "20" },
  { value: 21, label: "21" },
  { value: 22, label: "22" },
  { value: 23, label: "23" },
  { value: 24, label: "24" },
];

export const workingDaysPerWeekOptions = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
];

export const getPaymentTermOptions = (t: any) => [
  { value: PaymentTerm.HOUR, label: t("Hour") },
  { value: PaymentTerm.DAY, label: t("Day") },
  { value: PaymentTerm.WEEK, label: t("Week") },
  { value: PaymentTerm.MONTH, label: t("Month") },
];

export const getAccountTypeOptions = (t: any) => [
  { value: AccountType.CASH, label: t("Cash") },
  { value: AccountType.BANK, label: t("Bank") },
  { value: AccountType.DIGITAL_WALLET, label: t("DigitalWallet") },
  { value: AccountType.POS, label: t("Pos") },
];

export const getStatusOptions = (t: any) => [
  { value: true, label: t("Active") },
  { value: false, label: t("Inactive") },
];

export const getWeekDays = (t: any) => [
  { value: 0, label: t("Sunday"), shortLabel: t("Sun") },
  { value: 1, label: t("Monday"), shortLabel: t("Mon") },
  { value: 2, label: t("Tuesday"), shortLabel: t("Tue") },
  { value: 3, label: t("Wednesday"), shortLabel: t("Wed") },
  { value: 4, label: t("Thursday"), shortLabel: t("Thu") },
  { value: 5, label: t("Friday"), shortLabel: t("Fri") },
  { value: 6, label: t("Saturday"), shortLabel: t("Sat") },
];

export const getMonths = (t: any) => [
  { value: 1, label: t("January") },
  { value: 2, label: t("February") },
  { value: 3, label: t("March") },
  { value: 4, label: t("April") },
  { value: 5, label: t("May") },
  { value: 6, label: t("June") },
  { value: 7, label: t("July") },
  { value: 8, label: t("August") },
  { value: 9, label: t("September") },
  { value: 10, label: t("October") },
  { value: 11, label: t("November") },
  { value: 12, label: t("December") },
];

export const variantTypeOptions = [
  { value: VariantType.Text, label: VariantType.Text },
  { value: VariantType.SingleSelect, label: VariantType.SingleSelect },
  { value: VariantType.MultiSelect, label: VariantType.MultiSelect },
];

export const tableStatusColor: Record<string, string> = {
  [TableStatus.VACANT]: "#4caf50",
  // [TableStatus.PARTIALLY_VACANT]: "#c5e1a5",
  [TableStatus.PARTIALLY_VACANT]: "#a1ca71",
  [TableStatus.DINING]: "#ff9800",
  [TableStatus.CLEANING]: "#9e9e9e",
};

export const kitchenProductStatusColor: Record<string, string> = {
  [KitchenProductStatus.WAITING]: "#f8ccccff",
  [KitchenProductStatus.ACCEPTED]: "#faeacdff",
};

export const orderProductStatusColor: Record<string, string> = {
  [OrderProductStatus.WAITING]: "background.paper",
  [OrderProductStatus.ACCEPTED]: "#faeacdff",
  [OrderProductStatus.DECLINED]: "#f8ccccff",
  // [OrderProductStatus.COOKED]: "#f8ccccff",
  [OrderProductStatus.COOKED]: "#fabebe",
  [OrderProductStatus.SERVED]: "#dcf6e1",
  // [OrderProductStatus.DECLINED]: "#ff9b9b",
};

export const templates = [
  {
    id: "template1",
    label: "Template 1",
  },
  {
    id: "template2",
    label: "Template 2",
  },
  {
    id: "template3",
    label: "Template 3",
  },
  {
    id: "template4",
    label: "Template 4",
  },
  {
    id: "template5",
    label: "Template 5",
  },
  {
    id: "template6",
    label: "Template 6",
  },
  {
    id: "template7",
    label: "Template 7",
  },
  {
    id: "Template8",
    label: "Template 8",
  },
  {
    id: "Template9",
    label: "Template 9",
  },
  {
    id: "Template10",
    label: "Template 10",
  },
];

const mostUsedFeatures = [
  {
    title: "CloudBilling",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "InventoryManagement",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "Sales",
    basic: true,
    growth: true,
    scale: true,
  },
  { title: "Counter&Cashier", basic: true, growth: true, scale: true },
  {
    title: "Reports",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "Order",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "Purchase",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "ExpenseManagement",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "IncomeManagement",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "Usage",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "WasteManagement",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "Accounts",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "StockCounts",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "ProductionCosting",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "DeliveryAppPricing",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "StockTransfer",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "Quotation",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "CRM",
    basic: false,
    growth: false,
    scale: true,
  },
  { title: "LoyaltyPoints", basic: false, growth: false, scale: true },
  { title: "Offers", basic: false, growth: false, scale: true },
  { title: "WhatsAppBill", basic: false, growth: false, scale: true },
  { title: "EmailBill", basic: false, growth: false, scale: true },
  { title: "WhatsAppSale", basic: false, growth: false, scale: true },
  {
    title: "WorkHistory",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "CustomerFeedback",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "E-Invoice",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "E-WayBill",
    basic: false,
    growth: false,
    scale: true,
  },
];

const mostUsedFeaturesWithInvoice = [
  {
    title: "CloudBilling",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "InventoryManagement",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "Sales",
    basic: true,
    growth: true,
    scale: true,
  },
  { title: "Counter&Cashier", basic: true, growth: true, scale: true },
  {
    title: "Reports",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "Order",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "Invoice",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "Purchase",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "ExpenseManagement",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "IncomeManagement",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "Usage",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "WasteManagement",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "Accounts",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "StockCounts",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "ProductionCosting",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "DeliveryAppPricing",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "StockTransfer",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "Quotation",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "CRM",
    basic: false,
    growth: false,
    scale: true,
  },
  { title: "LoyaltyPoints", basic: false, growth: false, scale: true },
  { title: "Offers", basic: false, growth: false, scale: true },
  { title: "WhatsAppBill", basic: false, growth: false, scale: true },
  { title: "EmailBill", basic: false, growth: false, scale: true },
  { title: "WhatsAppSale", basic: false, growth: false, scale: true },
  {
    title: "WorkHistory",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "CustomerFeedback",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "E-Invoice",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "E-WayBill",
    basic: false,
    growth: false,
    scale: true,
  },
];

const restaurantFeatures = [
  {
    title: "CloudBilling",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "InventoryManagement",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "KOTManagement",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "Sales",
    basic: true,
    growth: true,
    scale: true,
  },
  { title: "Counter&Cashier", basic: true, growth: true, scale: true },
  {
    title: "CustomerFeedback",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "Reports",
    basic: true,
    growth: true,
    scale: true,
  },
  {
    title: "Purchase",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "ExpenseManagement",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "IncomeManagement",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "Usage",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "WasteManagement",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "CaptainApp",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "OutdoorOrder",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "Accounts",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "StockCounts",
    basic: false,
    growth: true,
    scale: true,
  },
  {
    title: "RecipeCosting",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "DeliveryAppPricing",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "KitchenDisplaySystem",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "KitchenStore",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "StockTransfer",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "Quotation",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "CRM",
    basic: false,
    growth: false,
    scale: true,
  },
  { title: "LoyaltyPoints", basic: false, growth: false, scale: true },
  { title: "Offers", basic: false, growth: false, scale: true },
  { title: "WhatsAppBill", basic: false, growth: false, scale: true },
  { title: "EmailBill", basic: false, growth: false, scale: true },
  { title: "WhatsAppSale", basic: false, growth: false, scale: true },
  {
    title: "WorkHistory",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "E-Invoice",
    basic: false,
    growth: false,
    scale: true,
  },
  {
    title: "E-WayBill",
    basic: false,
    growth: false,
    scale: true,
  },
];

const foodIndustryFeatures = mostUsedFeatures.map((f: any) =>
  f.title === "ProductionCosting" ? { ...f, title: "RecipeCosting" } : f,
);

export const appSubscriptions = [
  {
    app: App.ACCOUNTS,
    price: {
      IN: { basic: 499, growth: 599, scale: 799 },
      US: { basic: 29, growth: 79, scale: 149 },
      CA: { basic: 35, growth: 89, scale: 169 },
      GB: { basic: 25, growth: 69, scale: 129 },
      AU: { basic: 45, growth: 119, scale: 229 },
      DE: { basic: 29, growth: 79, scale: 149 },
      FR: { basic: 29, growth: 79, scale: 149 },
      NL: { basic: 29, growth: 79, scale: 149 },
      SG: { basic: 39, growth: 99, scale: 189 },
      JP: { basic: 3900, growth: 9900, scale: 19900 },
      KR: { basic: 39000, growth: 99000, scale: 199000 },
      AE: { basic: 109, growth: 279, scale: 549 },
      SA: { basic: 99, growth: 249, scale: 499 },
      MY: { basic: 69, growth: 169, scale: 329 },
      TH: { basic: 790, growth: 1990, scale: 3790 },
      ZA: { basic: 699, growth: 1599, scale: 2999 },
      BR: { basic: 79, growth: 179, scale: 349 },
      MX: { basic: 599, growth: 1399, scale: 2699 },
      ID: { basic: 49000, growth: 99000, scale: 199000 },
      PH: { basic: 990, growth: 2490, scale: 4490 },
      COMMON: { basic: 29, growth: 79, scale: 149 },
    },
    features: [
      {
        title: "ExpenseManagement",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "IncomeManagement",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "CustomerManagement",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "Accounts",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "Usage",
        basic: false,
        growth: true,
        scale: true,
      },
      {
        title: "WorkHistory",
        basic: false,
        growth: false,
        scale: true,
      },
    ],
  },
  {
    app: App.PAYROLL,
    price: {
      IN: { basic: 499, growth: 999, scale: 1999 },
      US: { basic: 29, growth: 79, scale: 149 },
      CA: { basic: 35, growth: 89, scale: 169 },
      GB: { basic: 25, growth: 69, scale: 129 },
      AU: { basic: 45, growth: 119, scale: 229 },
      DE: { basic: 29, growth: 79, scale: 149 },
      FR: { basic: 29, growth: 79, scale: 149 },
      NL: { basic: 29, growth: 79, scale: 149 },
      SG: { basic: 39, growth: 99, scale: 189 },
      JP: { basic: 3900, growth: 9900, scale: 19900 },
      KR: { basic: 39000, growth: 99000, scale: 199000 },
      AE: { basic: 109, growth: 279, scale: 549 },
      SA: { basic: 99, growth: 249, scale: 499 },
      MY: { basic: 69, growth: 169, scale: 329 },
      TH: { basic: 790, growth: 1990, scale: 3790 },
      ZA: { basic: 699, growth: 1599, scale: 2999 },
      BR: { basic: 79, growth: 179, scale: 349 },
      MX: { basic: 599, growth: 1399, scale: 2699 },
      ID: { basic: 49000, growth: 99000, scale: 199000 },
      PH: { basic: 990, growth: 2490, scale: 4490 },
      COMMON: { basic: 29, growth: 79, scale: 149 },
    },
    features: [
      {
        title: "BiometricAttendance",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "ManualAttendance",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "WorkLogManagement",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "BulkAttendance",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "DutyChart",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "ShiftManagement",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "SalaryBasedOnAttendance",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "SalaryBasedOnWorkDone",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "AdvanceManagement",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "Usage",
        basic: false,
        growth: true,
        scale: true,
      },
      {
        title: "WorkHistory",
        basic: false,
        growth: false,
        scale: true,
      },
    ],
  },
  {
    app: App.CRM,
    price: {
      IN: { basic: 499, growth: 999, scale: 1999 },
      US: { basic: 29, growth: 79, scale: 149 },
      CA: { basic: 35, growth: 89, scale: 169 },
      GB: { basic: 25, growth: 69, scale: 129 },
      AU: { basic: 45, growth: 119, scale: 229 },
      DE: { basic: 29, growth: 79, scale: 149 },
      FR: { basic: 29, growth: 79, scale: 149 },
      NL: { basic: 29, growth: 79, scale: 149 },
      SG: { basic: 39, growth: 99, scale: 189 },
      JP: { basic: 3900, growth: 9900, scale: 19900 },
      KR: { basic: 39000, growth: 99000, scale: 199000 },
      AE: { basic: 109, growth: 279, scale: 549 },
      SA: { basic: 99, growth: 249, scale: 499 },
      MY: { basic: 69, growth: 169, scale: 329 },
      TH: { basic: 790, growth: 1990, scale: 3790 },
      ZA: { basic: 699, growth: 1599, scale: 2999 },
      BR: { basic: 79, growth: 179, scale: 349 },
      MX: { basic: 599, growth: 1399, scale: 2699 },
      ID: { basic: 49000, growth: 99000, scale: 199000 },
      PH: { basic: 990, growth: 2490, scale: 4490 },
      COMMON: { basic: 29, growth: 79, scale: 149 },
    },
    features: [
      {
        title: "WhatsappMarketing",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "EmailMarketing",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "SmsMarketing",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "CustomerManagement",
        basic: true,
        growth: true,
        scale: true,
      },
      { title: "LoyaltyPoints", basic: false, growth: false, scale: true },
      { title: "Offers", basic: false, growth: false, scale: true },
      {
        title: "Usage",
        basic: false,
        growth: true,
        scale: true,
      },
      {
        title: "WorkHistory",
        basic: false,
        growth: false,
        scale: true,
      },
    ],
  },
  {
    app: App.RESTAURANT,
    price: {
      IN: { basic: 1499, growth: 2499, scale: 3999 },
      US: { basic: 199, growth: 349, scale: 499 },
      CA: { basic: 239, growth: 419, scale: 599 },
      GB: { basic: 169, growth: 299, scale: 429 },
      AU: { basic: 299, growth: 499, scale: 699 },
      DE: { basic: 199, growth: 349, scale: 499 },
      FR: { basic: 199, growth: 349, scale: 499 },
      NL: { basic: 199, growth: 349, scale: 499 },
      SG: { basic: 269, growth: 449, scale: 649 },
      JP: { basic: 26900, growth: 46900, scale: 69900 },
      KR: { basic: 269000, growth: 469000, scale: 699000 },
      AE: { basic: 699, growth: 1199, scale: 1899 },
      SA: { basic: 649, growth: 1099, scale: 1799 },
      MY: { basic: 329, growth: 549, scale: 799 },
      TH: { basic: 3990, growth: 6990, scale: 10990 },
      ZA: { basic: 2999, growth: 4999, scale: 7999 },
      BR: { basic: 349, growth: 599, scale: 899 },
      MX: { basic: 2699, growth: 4699, scale: 6999 },
      ID: { basic: 199000, growth: 349000, scale: 549000 },
      PH: { basic: 4490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: restaurantFeatures,
  },
  {
    app: App.BAKERY,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2499 },
      US: { basic: 149, growth: 249, scale: 399 },
      CA: { basic: 179, growth: 299, scale: 479 },
      GB: { basic: 129, growth: 219, scale: 349 },
      AU: { basic: 219, growth: 369, scale: 599 },
      DE: { basic: 149, growth: 249, scale: 399 },
      FR: { basic: 149, growth: 249, scale: 399 },
      NL: { basic: 149, growth: 249, scale: 399 },
      SG: { basic: 199, growth: 329, scale: 529 },
      JP: { basic: 19900, growth: 33900, scale: 53900 },
      KR: { basic: 199000, growth: 339000, scale: 539000 },
      AE: { basic: 549, growth: 899, scale: 1499 },
      SA: { basic: 499, growth: 849, scale: 1399 },
      MY: { basic: 249, growth: 419, scale: 649 },
      TH: { basic: 2990, growth: 4990, scale: 7990 },
      ZA: { basic: 1999, growth: 3499, scale: 5499 },
      BR: { basic: 269, growth: 449, scale: 699 },
      MX: { basic: 1999, growth: 3399, scale: 5499 },
      ID: { basic: 149000, growth: 249000, scale: 399000 },
      PH: { basic: 3490, growth: 5990, scale: 9490 },
      COMMON: { basic: 99, growth: 199, scale: 349 },
    },
    features: foodIndustryFeatures,
  },
  {
    app: App.SUPERMARKET,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3499 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.MILL,
    price: {
      IN: { basic: 1499, growth: 2499, scale: 3499 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.FURNITURE,
    price: {
      IN: { basic: 999, growth: 1799, scale: 2999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.PHARMACY,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3499 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.BAR,
    price: {
      IN: { basic: 1300, growth: 2299, scale: 3499 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: foodIndustryFeatures,
  },
  {
    app: App.RESTOBAR,
    price: {
      IN: { basic: 1499, growth: 2499, scale: 3999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: restaurantFeatures.filter((f) => f.title !== "OutdoorOrder"),
  },
  {
    app: App.MOBILESHOP,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2999 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeaturesWithInvoice,
  },
  {
    app: App.FINANCE,
    price: {
      IN: { basic: 1499, growth: 2499, scale: 4499 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: [
      {
        title: "loan",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "loanCollection",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "lines",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "routes",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "visits",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "devices",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "KYC",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "documents",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "Reports",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "ExpenseManagement",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "IncomeManagement",
        basic: true,
        growth: true,
        scale: true,
      },
      {
        title: "Usage",
        basic: false,
        growth: true,
        scale: true,
      },
      {
        title: "Accounts",
        basic: false,
        growth: true,
        scale: true,
      },
      {
        title: "CRM",
        basic: false,
        growth: false,
        scale: true,
      },
      { title: "LoyaltyPoints", basic: false, growth: false, scale: true },
      { title: "Offers", basic: false, growth: false, scale: true },
      {
        title: "WorkHistory",
        basic: false,
        growth: false,
        scale: true,
      },
    ],
  },
  {
    app: App.BRICKS,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3499 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.POULTRY,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3499 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.TEXTILES,
    price: {
      IN: { basic: 1499, growth: 1999, scale: 3999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.JEWELLERY,
    price: {
      IN: { basic: 1499, growth: 2999, scale: 4999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.ELECTRONICS,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.HARDWARE,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.STATIONERY,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2499 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.GIFTSHOP,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2499 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.ICECREAM,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2499 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: foodIndustryFeatures,
  },
  {
    app: App.FOOTWEAR,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2499 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.FRUITS,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2499 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.SPORTS,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2999 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.PAINT,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.PETS,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2999 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.BAGS,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2999 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeaturesWithInvoice,
  },
  {
    app: App.COSMETICS,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2999 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.WATCHSHOP,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2999 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeaturesWithInvoice,
  },
  {
    app: App.OPTICALS,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeaturesWithInvoice,
  },
  {
    app: App.PERFUME,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2999 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.SILVERWARE,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.TYRESHOP,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeaturesWithInvoice,
  },
  {
    app: App.FERTILIZERS,
    price: {
      IN: { basic: 999, growth: 2499, scale: 4499 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.BICYCLESHOP,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2999 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeaturesWithInvoice,
  },
  {
    app: App.STEELS,
    price: {
      IN: { basic: 1500, growth: 2500, scale: 5000 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.PLYWOOD,
    price: {
      IN: { basic: 1000, growth: 2000, scale: 4000 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.LIGHTSHOP,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.SNACKSHOP,
    price: {
      IN: { basic: 899, growth: 1399, scale: 2499 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: foodIndustryFeatures,
  },
  {
    app: App.TILESSTORE,
    price: {
      IN: { basic: 999, growth: 2499, scale: 4999 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeatures,
  },
  {
    app: App.LAPTOPSTORE,
    price: {
      IN: { basic: 999, growth: 1999, scale: 3499 },
      US: { basic: 149, growth: 349, scale: 499 },
      CA: { basic: 179, growth: 419, scale: 599 },
      GB: { basic: 129, growth: 299, scale: 429 },
      AU: { basic: 219, growth: 499, scale: 699 },
      DE: { basic: 149, growth: 349, scale: 499 },
      FR: { basic: 149, growth: 349, scale: 499 },
      NL: { basic: 149, growth: 349, scale: 499 },
      SG: { basic: 199, growth: 449, scale: 649 },
      JP: { basic: 19900, growth: 46900, scale: 69900 },
      KR: { basic: 199000, growth: 469000, scale: 699000 },
      AE: { basic: 549, growth: 1199, scale: 1899 },
      SA: { basic: 499, growth: 1099, scale: 1799 },
      MY: { basic: 249, growth: 549, scale: 799 },
      TH: { basic: 2990, growth: 6990, scale: 10990 },
      ZA: { basic: 1999, growth: 4999, scale: 7999 },
      BR: { basic: 269, growth: 599, scale: 899 },
      MX: { basic: 1999, growth: 4699, scale: 6999 },
      ID: { basic: 149000, growth: 349000, scale: 549000 },
      PH: { basic: 3490, growth: 7490, scale: 10990 },
      COMMON: { basic: 99, growth: 249, scale: 399 },
    },
    features: mostUsedFeaturesWithInvoice,
  },
  {
    app: App.REPAIRSERVICES,
    price: {
      IN: { basic: 999, growth: 1499, scale: 2499 },
      US: { basic: 99, growth: 249, scale: 399 },
      CA: { basic: 119, growth: 299, scale: 479 },
      GB: { basic: 89, growth: 219, scale: 349 },
      AU: { basic: 149, growth: 369, scale: 599 },
      DE: { basic: 99, growth: 249, scale: 399 },
      FR: { basic: 99, growth: 249, scale: 399 },
      NL: { basic: 99, growth: 249, scale: 399 },
      SG: { basic: 139, growth: 329, scale: 529 },
      JP: { basic: 13900, growth: 33900, scale: 53900 },
      KR: { basic: 139000, growth: 339000, scale: 539000 },
      AE: { basic: 369, growth: 899, scale: 1499 },
      SA: { basic: 349, growth: 849, scale: 1399 },
      MY: { basic: 179, growth: 419, scale: 649 },
      TH: { basic: 1990, growth: 4990, scale: 7990 },
      ZA: { basic: 1499, growth: 3499, scale: 5499 },
      BR: { basic: 199, growth: 449, scale: 699 },
      MX: { basic: 1499, growth: 3399, scale: 5499 },
      ID: { basic: 99000, growth: 249000, scale: 399000 },
      PH: { basic: 2490, growth: 5990, scale: 9490 },
      COMMON: { basic: 69, growth: 149, scale: 299 },
    },
    features: mostUsedFeaturesWithInvoice,
  },
];

export const commonFeatures = [
  "BarcodeScanning",
  "DetailedDataAnalytics",
  "MultipleLanguageSupport",
  "AutomaticDataBackup",
  "IndividualConfiguration",
  "AccessInAnyDevice",
  "NoSetupOrHiddenFees",
  "PremiumSupport",
  "FreeUpdates",
];

export const myAppSubscription = {
  price: {
    IN: {
      sales: 700,
      order: 700,
      invoice: 500,
      restaurant: 2000,
      staff: 700,
      expense: 500,
      accounts: 300,
      history: 200,
    },
    US: {
      sales: 70,
      order: 70,
      invoice: 50,
      restaurant: 150,
      staff: 70,
      expense: 50,
      accounts: 50,
      history: 70,
    },
    CA: {
      sales: 85,
      order: 85,
      invoice: 60,
      restaurant: 180,
      staff: 85,
      expense: 60,
      accounts: 60,
      history: 80,
    },
    GB: {
      sales: 60,
      order: 60,
      invoice: 45,
      restaurant: 130,
      staff: 60,
      expense: 45,
      accounts: 45,
      history: 60,
    },
    AU: {
      sales: 95,
      order: 95,
      invoice: 70,
      restaurant: 210,
      staff: 95,
      expense: 70,
      accounts: 70,
      history: 90,
    },
    DE: {
      sales: 70,
      order: 70,
      invoice: 50,
      restaurant: 150,
      staff: 70,
      expense: 50,
      accounts: 50,
      history: 70,
    },
    FR: {
      sales: 70,
      order: 70,
      invoice: 50,
      restaurant: 150,
      staff: 70,
      expense: 50,
      accounts: 50,
      history: 70,
    },
    NL: {
      sales: 70,
      order: 70,
      invoice: 50,
      restaurant: 150,
      staff: 70,
      expense: 50,
      accounts: 50,
      history: 70,
    },
    SG: {
      sales: 80,
      order: 80,
      invoice: 60,
      restaurant: 170,
      staff: 80,
      expense: 60,
      accounts: 60,
      history: 75,
    },
    JP: {
      sales: 7000,
      order: 7000,
      invoice: 5000,
      restaurant: 20000,
      staff: 7000,
      expense: 5000,
      accounts: 3000,
      history: 2000,
    },
    KR: {
      sales: 70000,
      order: 70000,
      invoice: 50000,
      restaurant: 200000,
      staff: 70000,
      expense: 50000,
      accounts: 30000,
      history: 20000,
    },
    AE: {
      sales: 260,
      order: 260,
      invoice: 180,
      restaurant: 650,
      staff: 260,
      expense: 180,
      accounts: 150,
      history: 140,
    },
    SA: {
      sales: 240,
      order: 240,
      invoice: 170,
      restaurant: 600,
      staff: 240,
      expense: 170,
      accounts: 140,
      history: 130,
    },
    MY: {
      sales: 120,
      order: 120,
      invoice: 90,
      restaurant: 300,
      staff: 120,
      expense: 90,
      accounts: 80,
      history: 90,
    },
    TH: {
      sales: 1400,
      order: 1400,
      invoice: 1000,
      restaurant: 4000,
      staff: 1400,
      expense: 1000,
      accounts: 800,
      history: 700,
    },
    ZA: {
      sales: 800,
      order: 800,
      invoice: 600,
      restaurant: 2200,
      staff: 800,
      expense: 600,
      accounts: 500,
      history: 400,
    },
    BR: {
      sales: 100,
      order: 100,
      invoice: 80,
      restaurant: 250,
      staff: 100,
      expense: 80,
      accounts: 70,
      history: 80,
    },
    MX: {
      sales: 900,
      order: 900,
      invoice: 700,
      restaurant: 2500,
      staff: 900,
      expense: 700,
      accounts: 600,
      history: 500,
    },
    ID: {
      sales: 70000,
      order: 70000,
      invoice: 50000,
      restaurant: 200000,
      staff: 70000,
      expense: 50000,
      accounts: 30000,
      history: 20000,
    },
    PH: {
      sales: 1400,
      order: 1400,
      invoice: 1000,
      restaurant: 3500,
      staff: 1400,
      expense: 1000,
      accounts: 800,
      history: 700,
    },
    COMMON: {
      sales: 50,
      order: 50,
      invoice: 70,
      restaurant: 120,
      staff: 50,
      expense: 30,
      accounts: 30,
      history: 50,
    },
  },
};

export const savePrice = {
  IN: { basic: 500, growth: 1510, scale: 3510 },
  US: { basic: 50, growth: 100, scale: 300 },
  CA: { basic: 60, growth: 120, scale: 350 },
  GB: { basic: 45, growth: 90, scale: 280 },
  AU: { basic: 75, growth: 140, scale: 380 },
  DE: { basic: 50, growth: 100, scale: 300 },
  FR: { basic: 50, growth: 100, scale: 300 },
  NL: { basic: 50, growth: 100, scale: 300 },
  SG: { basic: 65, growth: 130, scale: 360 },
  JP: { basic: 5000, growth: 10000, scale: 30000 },
  KR: { basic: 50000, growth: 100000, scale: 300000 },
  AE: { basic: 180, growth: 350, scale: 900 },
  SA: { basic: 170, growth: 330, scale: 850 },
  MY: { basic: 90, growth: 180, scale: 450 },
  TH: { basic: 1000, growth: 2000, scale: 5000 },
  ZA: { basic: 600, growth: 1200, scale: 3500 },
  BR: { basic: 80, growth: 160, scale: 420 },
  MX: { basic: 700, growth: 1400, scale: 3800 },
  ID: { basic: 50000, growth: 100000, scale: 300000 },
  PH: { basic: 1000, growth: 2000, scale: 5000 },
  COMMON: { basic: 50, growth: 100, scale: 300 },
};

export const myAppSavePrice = {
  IN: {
    monthly: 500,
    yearly: 3510,
  },
  US: {
    monthly: 50,
    yearly: 400,
  },
  CA: {
    monthly: 60,
    yearly: 450,
  },
  GB: {
    monthly: 45,
    yearly: 350,
  },
  AU: {
    monthly: 75,
    yearly: 520,
  },
  DE: {
    monthly: 50,
    yearly: 400,
  },
  FR: {
    monthly: 50,
    yearly: 400,
  },
  NL: {
    monthly: 50,
    yearly: 400,
  },
  SG: {
    monthly: 65,
    yearly: 480,
  },
  JP: {
    monthly: 5000,
    yearly: 40000,
  },
  KR: {
    monthly: 50000,
    yearly: 400000,
  },
  AE: {
    monthly: 180,
    yearly: 1200,
  },
  SA: {
    monthly: 170,
    yearly: 1150,
  },
  MY: {
    monthly: 90,
    yearly: 650,
  },
  TH: {
    monthly: 1000,
    yearly: 7000,
  },
  ZA: {
    monthly: 600,
    yearly: 4200,
  },
  BR: {
    monthly: 80,
    yearly: 600,
  },
  MX: {
    monthly: 700,
    yearly: 4800,
  },
  ID: {
    monthly: 50000,
    yearly: 400000,
  },
  PH: {
    monthly: 1000,
    yearly: 7000,
  },
  COMMON: {
    monthly: 50,
    yearly: 400,
  },
};

export const defaultCurrency = "USD";
export const yearlyDiscountPercentage = 10;
export const branchPercentage = 35;
export const processingFeePercentage = 2;

export const taxEngine = {
  IN: {
    isApplicable: true,
    type: "GST",
    rate: 18,
  },
  US: {
    isApplicable: false,
    type: "",
    rate: 0,
  },
};

const pricingTiers = {
  tier1: [
    "US",
    "GB",
    "AU",
    "CA",
    "SG",
    "CH",
    "DK",
    "NO",
    "SE",
    "NZ",
    "HK",
    "JP",
    "KR",
    "IL",
    "IS",
    "KW",
    "AE",
    "QA",
    "SA",
    "OM",
    "BH",
    "BM",
    "BS",
    "KY",
    "FK",
    "GI",
    "DE",
    "FR",
    "IT",
    "ES",
    "NL",
    "BE",
    "AT",
    "IE",
    "FI",
    "PT",
    "LU",
    "GR",
  ],
  tier2: [
    "MY",
    "CN",
    "BR",
    "MX",
    "PL",
    "CZ",
    "RO",
    "HU",
    "HR",
    "BG",
    "TR",
    "ZA",
    "TW",
    "MO",
    "JO",
    "KZ",
    "GE",
    "AZ",
    "BW",
    "BB",
    "AW",
    "CW",
    "SX",
    "TT",
    "UY",
    "PE",
    "CL",
    "CO",
    "CR",
    "BZ",
    "CV",
    "EE",
    "LT",
    "LV",
  ],
  tier3: [
    "IN",
    "TH",
    "PH",
    "ID",
    "VN",
    "LK",
    "NP",
    "PK",
    "BD",
    "EG",
    "MA",
    "DZ",
    "TN",
    "UA",
    "RS",
    "MK",
    "MU",
    "MV",
    "SC",
    "FJ",
    "GT",
    "DO",
    "SV",
    "PA",
    "PY",
    "BO",
    "NI",
    "HN",
    "JM",
    "TO",
    "WS",
  ],
  tier4: [
    "AF",
    "AL",
    "AM",
    "AO",
    "AR",
    "BI",
    "CD",
    "DJ",
    "ER",
    "ET",
    "GH",
    "GM",
    "GN",
    "GY",
    "HT",
    "IQ",
    "IR",
    "KE",
    "KG",
    "KH",
    "KM",
    "LA",
    "LB",
    "LR",
    "LY",
    "MD",
    "MG",
    "MM",
    "MN",
    "MZ",
    "MW",
    "NG",
    "PG",
    "RW",
    "SB",
    "SO",
    "SR",
    "SZ",
    "TJ",
    "TM",
    "TZ",
    "UG",
    "UZ",
    "VU",
    "YE",
  ],
  tier5: [
    "BY",
    "CU",
    "KP",
    "MR",
    "RU",
    "SD",
    "SL",
    "SY",
    "VE",
    "ZM",
    "ZW",
    "ST",
  ],
};
